import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Compass, HelpCircle } from "lucide-react";

interface SourceRow {
  source: string;
  signups: number;
  paid: number;
  conversion: number;
}

interface Payload {
  success: true;
  data: {
    rangeDays: number;
    totals: { signups: number; paid: number; conversion: number };
    bySource: SourceRow[];
    unattributedLegacy: number;
  };
}

const SOURCE_LABELS: Record<string, string> = {
  google: "Google (organic / ads)",
  search: "Bing / DuckDuckGo",
  meta: "Meta (Facebook / Instagram / m.me)",
  whatsapp: "WhatsApp link",
  twitter: "X / Twitter",
  tiktok: "TikTok",
  direct: "Direct (typed URL / bookmark)",
  manychat: "ManyChat flow",
  unknown: "Unknown (pre-fix account)",
};

function labelFor(source: string): string {
  return SOURCE_LABELS[source] ?? source;
}

function pct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

export default function AdminSignupAttribution() {
  const [days, setDays] = useState("30");

  const { data, isLoading } = useQuery<Payload>({
    queryKey: [`/api/admin/signup-attribution?days=${days}`],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/admin/signup-attribution?days=${days}`);
      return res.json();
    },
  });

  const totals = data?.data?.totals;
  const rows = data?.data?.bySource ?? [];
  const legacy = data?.data?.unattributedLegacy ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Compass className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-semibold">Signup Attribution</h1>
            <p className="text-sm text-muted-foreground">
              Where new accounts are coming from. Captured from URL utm params, then first-touch document.referrer, then final fallback to "direct". Attribution pipe went live 2026-07-07, accounts before that show as "Unknown (pre-fix)".
            </p>
          </div>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="60">Last 60 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="180">Last 180 days</SelectItem>
            <SelectItem value="365">Last 365 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total signups</CardDescription>
            <CardTitle className="text-3xl">{isLoading ? "…" : totals?.signups ?? 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>New paid subs</CardDescription>
            <CardTitle className="text-3xl">{isLoading ? "…" : totals?.paid ?? 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Conversion (signup → paid)</CardDescription>
            <CardTitle className="text-3xl">{isLoading ? "…" : pct(totals?.conversion ?? 0)}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" /> Source breakdown
          </CardTitle>
          <CardDescription>
            Sorted by signup volume. Conversion is the share of that source's signups that turned paid.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No signups in this range.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Signups</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Conversion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.source}>
                    <TableCell>
                      <div className="font-medium">{labelFor(r.source)}</div>
                      <div className="text-xs text-muted-foreground font-mono">{r.source}</div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{r.signups}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.paid}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.signups > 0 ? (
                        <span className={r.conversion >= 0.15 ? "text-green-600" : r.conversion >= 0.05 ? "" : "text-muted-foreground"}>
                          {pct(r.conversion)}
                        </span>
                      ) : (
                        "–"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {legacy > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <HelpCircle className="h-4 w-4" /> Legacy unattributed accounts
            </CardDescription>
            <CardTitle className="text-xl">{legacy.toLocaleString()}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Accounts created before the attribution fix shipped on 2026-07-07. We can't know where they came from without a PostHog backfill against $initial_referrer per email. Not counted in the range totals above.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
